"use client";

import { useState, useCallback } from "react";
import { trackEvent } from "@/lib/analytics";

export interface FreeVhfPhraseSelfCheckOption {
  id: string;
  label: string;
}

export interface FreeVhfPhraseSelfCheckProps {
  /** Question text (e.g. "Which of these did you hear?"). */
  question: string;
  /** 2 - 4 options; no correct/incorrect feedback. */
  options: FreeVhfPhraseSelfCheckOption[];
  /** Optional id prefix for input name/ids (a11y). */
  idPrefix?: string;
  /** If true, use checkboxes (multiple selection); if false, radios (single). Default true  -  clips often contain several phrases. */
  multiple?: boolean;
  /** Optional hint below the question when multiple (e.g. "Select all that apply."). */
  selectAllHint?: string;
}

/**
 * M1/M2 phrase self-check: multiple-choice, no correct/incorrect feedback.
 * When multiple=true (default), use checkboxes (user can hear several phrases in one clip).
 */
export default function FreeVhfPhraseSelfCheck({
  question,
  options,
  idPrefix = "free-vhf-phrase-check",
  multiple = true,
  selectAllHint,
}: FreeVhfPhraseSelfCheckProps) {
  const [selectedSingle, setSelectedSingle] = useState<string | null>(null);
  const [selectedMultiple, setSelectedMultiple] = useState<string[]>([]);
  const name = `${idPrefix}-name`;

  const handleSingleChange = useCallback((id: string) => {
    setSelectedSingle(id);
    trackEvent("free_vhf_phrase_self_check", {
      question_id: idPrefix,
      value: id,
    });
  }, [idPrefix]);

  const handleMultipleChange = useCallback(
    (id: string, checked: boolean) => {
      setSelectedMultiple((prev) =>
        checked ? [...prev, id] : prev.filter((x) => x !== id)
      );
      trackEvent("free_vhf_phrase_self_check", {
        question_id: idPrefix,
        value: id,
        checked,
      });
    },
    [idPrefix]
  );

  return (
    <fieldset className="space-y-3">
      <legend className="text-lg font-medium text-content">{question}</legend>
      {selectAllHint && (
        <p className="text-sm text-muted -mt-1">{selectAllHint}</p>
      )}
      <div className="space-y-2">
        {options.map((opt) =>
          multiple ? (
            <label
              key={opt.id}
              className="flex items-center gap-3 cursor-pointer min-h-[44px]"
            >
              <input
                type="checkbox"
                name={name}
                value={opt.id}
                checked={selectedMultiple.includes(opt.id)}
                onChange={(e) =>
                  handleMultipleChange(opt.id, e.target.checked)
                }
                className="h-4 w-4 rounded border-[var(--neutral-border)] text-primary focus:ring-primary"
              />
              <span className="text-content">{opt.label}</span>
            </label>
          ) : (
            <label
              key={opt.id}
              className="flex items-center gap-3 cursor-pointer min-h-[44px]"
            >
              <input
                type="radio"
                name={name}
                value={opt.id}
                checked={selectedSingle === opt.id}
                onChange={() => handleSingleChange(opt.id)}
                className="h-4 w-4 border-[var(--neutral-border)] text-primary focus:ring-primary"
              />
              <span className="text-content">{opt.label}</span>
            </label>
          )
        )}
      </div>
    </fieldset>
  );
}
